import {HoistModel, PlainObject} from '@xh/hoist/core';
import {action, observable} from '@xh/hoist/mobx';
import {IndicatorParam} from '../../../data/ChartTypes';
import {getDefaultIndicatorParams, getIndicator, getIndicatorParams} from './TechnicalIndicators';

/**
 * Backs the gear popover used to tune the active indicator's parameters.
 *
 * Deliberately holds no copy of the parameter values - it reads them from props and pushes edits
 * straight back out, leaving the chart's settings form as the single source of truth. That keeps
 * the popover correct when the indicator is switched underneath it.
 */
export class IndicatorParamsPickerModel extends HoistModel {
    @observable accessor isOpen: boolean = false;

    get indicator(): string {
        return this.componentProps.indicator;
    }

    get indicatorLabel(): string {
        return getIndicator(this.indicator)?.label ?? '';
    }

    /** Parameters exposed by the active indicator. */
    get paramSpecs(): IndicatorParam[] {
        return getIndicatorParams(this.indicator);
    }

    /** Current values, with the indicator's own defaults filling any gaps. */
    get values(): PlainObject {
        return {...getDefaultIndicatorParams(this.indicator), ...(this.componentProps.params ?? {})};
    }

    get isDefault(): boolean {
        const defaults = getDefaultIndicatorParams(this.indicator),
            {values} = this;
        return Object.keys(defaults).every(key => JSON.stringify(defaults[key]) === JSON.stringify(values[key]));
    }

    @action
    open() {
        this.isOpen = true;
    }

    @action
    close() {
        this.isOpen = false;
    }

    setValue(name: string, value: any) {
        this.commit({...this.values, [name]: value});
    }

    /** Update a single entry of an array-valued param, e.g. APO's `periods: [10, 20]`. */
    setListValue(name: string, index: number, value: any) {
        const list = [...(this.values[name] ?? [])];
        list[index] = value;
        this.setValue(name, list);
    }

    resetToDefaults() {
        this.commit(getDefaultIndicatorParams(this.indicator));
    }

    private commit(params: PlainObject) {
        this.componentProps.onParamsChange?.(params);
    }
}
